import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowUpRight } from 'lucide-react';
import { useGiwahs } from '../context/GiwahsContext';
import { getSupplierById } from '../data/mockData';
import { VerificationBadge } from '../components/VerificationBadge';
import SEO from '../components/SEO';
import Img from '../components/Img';

const CompareSuppliers = () => {
  const { compareList, toggleCompare, clearCompare, hydrating } = useGiwahs();

  const items = compareList.map(getSupplierById).filter(Boolean);

  // Don't flash the empty state while the user's compare list is still loading
  // from Supabase (live mode) — wait for hydration to settle first.
  if (hydrating) {
    return (
      <div data-testid="compare-loading" className="surface-obsidian text-platinum">
        <div className="mx-auto max-w-2xl px-5 py-24 text-center md:py-32">
          <span className="label-overline text-neutral-500">Loading your compare list…</span>
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div data-testid="compare-empty" className="surface-obsidian text-platinum">
        <SEO title="Compare Suppliers" description="Compare multiple verified factories side-by-side on GIWAHS." noindex />
        <div className="mx-auto max-w-2xl px-5 py-24 text-center md:py-32">
          <span className="label-overline">/ Compare</span>
          <h1 className="mt-4 font-display text-4xl tracking-tighter md:text-6xl" style={{ fontWeight: 700 }}>Nothing to compare yet.</h1>
          <p className="mt-5 text-neutral-700">Add up to 4 suppliers from any directory or profile page to see them side-by-side.</p>
          <Link to="/factories" className="btn-giwahs btn-giwahs-primary mt-8 inline-flex">Browse factories</Link>
        </div>
      </div>
    );
  }

  const rows = [
    { label: 'Type', key: 'type' },
    { label: 'Location', render: (s) => `${s.city}, ${s.country}` },
    { label: 'Trust Score', render: (s) => `${s.trustScore}/100` },
    { label: 'MOQ Range', render: (s) => `${s.moqMin} – ${s.moqMax}` },
    { label: 'Lead Time', render: (s) => `${s.leadTimeDays} days` },
    { label: 'Capacity', key: 'productionCapacityPerMonth' },
    { label: 'Response Rate', render: (s) => `${s.responseRate}%` },
    { label: 'Pricing', key: 'pricingIndicator' },
    { label: 'Certifications', render: (s) => s.certifications.join(', ') },
    { label: 'Export Markets', render: (s) => s.exportMarkets.join(', ') },
    { label: 'Specialties', render: (s) => s.specialties.join(', ') },
  ];

  return (
    <div data-testid="compare-page" className="surface-obsidian text-platinum">
      <SEO title="Compare Suppliers" description="Side-by-side comparison of saved suppliers." noindex />
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1440px] px-5 py-12 md:px-8 md:py-16">
          <div className="flex items-end justify-between">
            <div>
              <span className="label-overline">/ Compare Suppliers</span>
              <h1 className="mt-4 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.25rem, 5vw, 4.5rem)', lineHeight: 0.95 }}>
                Side-by-side intelligence brief.
              </h1>
              <p className="mt-4 text-neutral-700">Comparing {items.length} supplier{items.length > 1 ? 's' : ''}.</p>
            </div>
            <button onClick={clearCompare} data-testid="compare-clear-all" className="label-overline border border-black px-3 py-2 hover:bg-black hover:text-white">Clear all</button>
          </div>
        </div>
      </section>

      <section className="surface-obsidian text-platinum">
        <div className="mx-auto max-w-[1440px] px-5 py-10 md:px-8 md:py-14">
          {/* Header strip */}
          <div className="overflow-x-auto border border-black">
            <table className="w-full min-w-[760px]">
              <thead>
                <tr className="border-b border-hairline surface-obsidian-2">
                  <th className="w-40 border-r border-neutral-200 p-4 text-left align-top label-overline">Supplier</th>
                  {items.map(s => (
                    <th key={s.id} className="border-r border-neutral-200 p-4 text-left align-top last:border-r-0" data-testid={`compare-col-${s.id}`}>
                      <div className="flex items-start justify-between gap-2">
                        <Link to={`/supplier/${s.slug}`} className="block">
                          <div className="aspect-[4/3] w-28 overflow-hidden border border-black bg-neutral-100 sm:w-36">
                            <Img src={s.heroImage} alt={`${s.name} — supplier comparison thumbnail`} variant="thumb" className="h-full w-full object-cover" />
                          </div>
                          <div className="mt-3 font-display text-lg leading-tight tracking-tight" style={{ fontWeight: 600 }}>{s.name}</div>
                        </Link>
                        <button onClick={() => toggleCompare(s.id)} className="flex h-7 w-7 items-center justify-center border border-black hover:bg-black hover:text-white" data-testid={`compare-remove-${s.id}`}>
                          <X size={12} strokeWidth={1.5} />
                        </button>
                      </div>
                      <div className="mt-3 flex flex-wrap gap-1">
                        {s.badges.slice(0, 3).map(b => <VerificationBadge key={b} type={b} />)}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={r.label} className={`border-b border-neutral-200 ${idx % 2 === 0 ? 'bg-white' : 'bg-[#FAFAF7]'}`}>
                    <td className="w-40 border-r border-neutral-200 p-4 align-top label-overline text-neutral-700">{r.label}</td>
                    {items.map(s => (
                      <td key={s.id} className="border-r border-neutral-200 p-4 align-top font-mono-data text-xs leading-relaxed last:border-r-0">
                        {r.render ? r.render(s) : s[r.key]}
                      </td>
                    ))}
                  </tr>
                ))}
                <tr>
                  <td className="border-r border-neutral-200 p-4 label-overline">Actions</td>
                  {items.map(s => (
                    <td key={s.id} className="border-r border-neutral-200 p-4 last:border-r-0">
                      <div className="flex flex-col gap-2">
                        <Link to={`/supplier/${s.slug}`} className="btn-giwahs btn-giwahs-secondary inline-flex items-center justify-center gap-1 text-center">
                          Profile <ArrowUpRight size={12} strokeWidth={1.5} />
                        </Link>
                        <Link to={`/rfq?supplier=${s.slug}`} className="btn-giwahs btn-giwahs-primary inline-flex items-center justify-center text-center">
                          RFQ
                        </Link>
                      </div>
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CompareSuppliers;
